/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { Gender } from 'common/types';
import SuspenseQuery from 'components/SuspenseQuery';
import ShareholdersOnlyCorporateOfficer from 'features/Onboarding/legacy/Pages/ShareholdersOnlyCorporateOfficer';
import { User } from 'features/Onboarding/legacy/types';
import { ApplicantType, ApplicationType } from 'features/Onboarding/types';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import OnboardingPageAdapter from '../../components/OnboardingPageAdapter';
import useOverrideSetCurrentPage from '../../hooks/useOverrideSetCurrentPage';
import getCurrentLegalBeneficiary from '../../lib/getCurrentLegalBeneficiary';
import Compliance from '../Compliance';
import PersonalAddress from '../PersonalAddress';
import { PersonalInfoModuleDataDocument } from './graphql/queries/personalInfoModuleData.gql';
import { getHasAlreadyFilledProfile, isSupposedDirector } from './libs';
import machineConfig, { model, State } from './machine';
import { Model } from './machine/model';
import BusinessRole from './pages/BusinessRole';
import PersonalName from './pages/PersonalName';
import SelectYourself from './pages/SelectYourself';

type PersonalInfoProps = {
  companyProfileId: string;
  applicationType: ApplicationType;
  applicantType: ApplicantType;
};

const PersonalInfo = asBentoModule<PersonalInfoProps>(
  ({ applicantType, applicationType, companyProfileId, onDone }) => {
    const personalInfoQuery = useQuery(PersonalInfoModuleDataDocument, {
      variables: {
        companyProfileId,
        includeSupposedBeneficiaries:
          applicationType === ApplicationType.Standard,
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={personalInfoQuery}
      >
        {(personalInfoData) => (
          <PersonalInfoComponent
            moduleData={{ ...personalInfoData, applicantType, applicationType }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const PersonalInfoComponent = withStateMachine<Model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;
    const { company, profile } = moduleData.viewer;

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const adaptedPageProps = {
      onPrev,
      setCurrentPage: useOverrideSetCurrentPage(send),
      user: profile as User,
    };

    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.SELECT_YOURSELF:
      case State.SEND_OFFICER_DATA: {
        if (!company) {
          throw new Error(`No company in state ${stringifiedState}`);
        }

        if (!company.supposedBeneficiaries) {
          throw new Error(
            `No supposed beneficiaries in state ${stringifiedState}`,
          );
        }

        return getHasAlreadyFilledProfile(profile) ? (
          <OBLoaderPage onPrev={() => null} />
        ) : (
          <SelectYourself
            directors={company.supposedBeneficiaries.filter(isSupposedDirector)}
            isSending={stringifiedState === State.SEND_OFFICER_DATA}
            onContinue={(director) =>
              send(model.events.SUBMIT_DIRECTOR_SELECTION(director))
            }
            onNonOfficerClick={() => send(model.events.SUBMIT_NON_OFFICER())}
            onNotFound={() => send(model.events.SUBMIT_NOT_FOUND())}
            onPrev={onPrev}
          />
        );
      }

      case State.ONLY_CORPORATE_OFFICER:
        return (
          <OnboardingPageAdapter
            {...adaptedPageProps}
            LegacyOnboardingPage={ShareholdersOnlyCorporateOfficer}
          />
        );

      case State.NAME:
      case State.SEND_NAME:
        return (
          <PersonalName
            applicationType={moduleData.applicationType}
            defaultValues={{
              firstName: profile.firstName ?? undefined,
              gender: (profile.gender as Gender) ?? undefined,
              lastName: profile.lastName ?? undefined,
            }}
            isSubmitting={stringifiedState === State.SEND_NAME}
            onPrev={onPrev}
            onSubmit={(payload) =>
              send(model.events.SUBMIT_PERSONAL_NAME(payload))
            }
          />
        );

      case State.BUSINESS_ROLE:
      case State.SEND_BUSINESS_ROLE: {
        const legalBeneficiary =
          company && getCurrentLegalBeneficiary(company.legalBeneficiaries);

        return (
          <BusinessRole
            defaultValues={{
              isDirector: legalBeneficiary?.isDirector ?? undefined,
              isMajorShareholder:
                legalBeneficiary?.isMajorShareholder ?? undefined,
            }}
            isSending={stringifiedState === State.SEND_BUSINESS_ROLE}
            onContinue={(payload) =>
              send(model.events.SUBMIT_BUSINESS_ROLE(payload))
            }
            onPrev={onPrev}
          />
        );
      }

      case State.PERSONAL_ADDRESS:
        return (
          <PersonalAddress
            applicantType={moduleData.applicantType}
            companyProfileId={company?.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? send(model.events.PREV())
                : send(model.events.NEXT())
            }
          />
        );

      case State.COMPLIANCE:
        return (
          <Compliance
            companyProfileId={company?.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? send(model.events.PREV())
                : send(model.events.NEXT())
            }
          />
        );

      case State.INIT:
      case State.ABORT_TO_RESET_PROMPT:
      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default PersonalInfo;
